import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { 
  getDecryptedValue, 
  generateHashedKey, 
  safeJsonParse, 
  useRelatedClaim,
  buildClaimsIndex,
  getClaimById,
  ErrorModal,
    useEobDownload,
    domainForSegment,
    useAnalytics
} from 'common';
import { ClaimResponse } from '@sentara-health/sentara-api-hooks-core';
import ClaimsBreakDownTable from './claimDetails/ClaimsBreakDown';
import ClaimsServiceTypeTable from './claimDetails/TypeOfService';
import { GlobalContext, isAvMed } from 'context';
import ClaimHeaderCard from './claimDetails/ClaimHeader';
import { Loader, Paragraph, Link } from '@sentara-health/optimahealth_web';
const SuperClaimsDetails: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { claimId, memberId, mId, memberName, showMedical } = location.state;
    const { serviceNumber } = useContext(GlobalContext);
    const { trackContentListing } = useAnalytics();

    const [claimData, setClaimData] = useState<ClaimResponse | null>();
  const [allClaims, setAllClaims] = useState<ClaimResponse[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [phone, setPhone] = useState<{
        data?: { benefitPhone1800?: string;};
    }>({});

    // Use the centralized EOB download hook
    const { downloadEobPdf, isDownloading, hasError, clearError } = useEobDownload(showMedical, mId);

    const trackClaimsLoadError = () => {
        const isBehavioralClaim = showMedical === false;
        const claimPath = isBehavioralClaim ? '/home/behavioral-claims' : '/home/medical-claims';
        const contentTitle = isBehavioralClaim ? 'Behavioral_claim_details_load_error_view' : 'Medical_claim_details_load_error_view';
        trackContentListing(
            isBehavioralClaim ? '1226' : '1224',
            contentTitle,
            isBehavioralClaim ? 18 : 16,
            `${domainForSegment}${claimPath}`
        );
    };


    const fetchClaimData = useCallback(async () => {
        setLoader(true);
        try {
            if (!memberId || !claimId) return;
      const claimKey = await generateHashedKey(
        `${showMedical ? 'Medical' : 'Behavioral'}_Claims`,
        memberId
      );
            const decryptedValue = await getDecryptedValue(claimKey);
        if (!decryptedValue) {
            trackClaimsLoadError();
            return;
        }

      const parsedData = safeJsonParse(decryptedValue, []);
      const claimsIndex = buildClaimsIndex(parsedData);
      const matchedClaim = getClaimById(claimsIndex, claimId);

        if (!matchedClaim) {
            trackClaimsLoadError();
        }

            setClaimData(matchedClaim ?? null);
      setAllClaims(parsedData);
        } catch (error) {
    trackClaimsLoadError();
      console.error('Error fetching claim data:', error);
        } finally {
            setLoader(false);
        }
    }, [memberId, claimId, showMedical, trackContentListing]);

  const relatedClaims = useRelatedClaim(
    claimData as ClaimResponse,
    allClaims,
    claimId ?? ''
  );

    useEffect(() => {
        fetchClaimData();
        setPhone(
            typeof serviceNumber === 'string'
                ? safeJsonParse(serviceNumber, {})
                : { data: serviceNumber }
        );
    }, [serviceNumber]);

    return (
        <div>
            {(loader || isDownloading) && <Loader />}
            
            {/* Error Modal for EOB download failures */}
            <ErrorModal 
                isOpen={hasError} 
                onClose={clearError}
            />
            
            {claimData && (
                <>
                    <ClaimHeaderCard
                        claimData={claimData}
                        claimId={claimId}
                        memberName={memberName}
                        showMedical={showMedical}
                        downloadEobPdf={downloadEobPdf}
                        isDownloadingPdf={isDownloading}
                        relatedClaims={relatedClaims}
                        mId={mId}
                    />
                    <ClaimsBreakDownTable claimData={claimData} />
                    <ClaimsServiceTypeTable claimData={claimData} />
                    {isAvMed() ? null : (
                        <div className="pt-4 print">
                            <Paragraph
                                text={
                                    <Trans
                                        i18nKey="medicalClaims.disclaimer"
                                        values={{
                                            phoneNumber1: phone?.data?.benefitPhone1800,
                                            email: t('medicalClaims.disclaimer6'),
                                            
                                        }}
                                        components={{
                                            phoneNumber1: (
                                                <Link
                                                    to={`tel:${phone?.data?.benefitPhone1800}`}
                                                    className="text-decoration-underline"
                                                />
                                            ),
                                            email: (
                                                <Link
                                                    to={`mailto:${t('medicalClaims.disclaimer6')}`}
                                                    className="text-decoration-underline"
                                                    data-testid="customerMailId"
                                                />
                                            ),
                                        }}
                                    />
                                }
                                variant="regular"
                                data-testid="combinedDisclaimer"
                                className="mb-5 lineBrake"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SuperClaimsDetails;
